/**
 * WEB-1599 (подтема 2) — единая печать момента отключения публичных страниц.
 *
 * Пояс зафиксирован явно (UTC) и подписан, время печатается вместе с датой:
 * доступ гаснет не «в конце дня», а в конкретный момент
 * (backend/src/billing/services/tier-enforcement.service.ts:
 * `Date.now() < pastDueSince + окно`). Раньше и письмо, и кабинет печатали
 * голую дату каждый в своём, нигде не зафиксированном поясе — совпадение
 * было случайностью, а у полуночи даты расходились на сутки.
 *
 * ⚠ ВТОРАЯ КОПИЯ ЭТОГО ПРАВИЛА живёт на сервере:
 * backend/src/billing/services/billing-email.service.ts (formatGraceDeadline).
 * Общего кода у реп нет, поэтому формат держится дословно совпадающими
 * ожиданиями в тестах обеих сторон (formatUtcDeadline.test.ts и
 * billing-email.service.spec.ts). Меняешь здесь — меняй там же, иначе письмо
 * и кабинет снова разойдутся, ради чего подтема и заводилась.
 *
 * Пояс организации в продукте нигде не хранится — выбор «UTC с явной
 * подписью» взят как решение по умолчанию из карточки.
 *
 * WEB-1874: дневная часть печатается на языке КАБИНЕТА (через
 * `i18n/format.ts`), а не всегда по-русски. Русский вариант совпадает с
 * прежним байт-в-байт, поэтому сходство с письмом сервера не нарушено.
 * ХВОСТ ЗАКРЫТ (этап 4): письмо тоже печатает срок на языке получателя —
 * `formatGraceDeadline` в `billing-email.service.ts` берёт язык из
 * `users.language` (`resolveEmailLocale`) и на английском даёт «August 29,
 * 2026, 12:00 UTC», то есть ровно то же, что кабинет: набор полей даты у
 * обеих копий один (day numeric + month long + year numeric, зона UTC).
 * Дословно совпадающими ожиданиями в тестах закреплена пока русская половина
 * (`formatUtcDeadline.test.ts` и `billing-email.service.spec.ts`) — английской
 * пары ожиданий на серверной стороне ещё нет.
 */
import { formatDate } from '@/i18n';

/** Подпись пояса. Вынесена, чтобы её нельзя было потерять правкой формата. */
export const UTC_LABEL = 'UTC';

/**
 * @returns «29 августа 2026 г., 12:00 UTC», либо null — если момента нет или
 * он неразбираем (вызывающий тогда показывает прежний текст без даты, а не
 * выдумывает её).
 */
export function formatUtcDeadline(
  value: string | number | Date | null | undefined,
): string | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const datePart = formatDate(date, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  // Часы/минуты берём геттерами UTC, а не через Intl: набор разделителей у
  // toLocaleString зависит от версии ICU (в одной «29 августа 2026 г. в
  // 12:00», в другой «29 августа 2026 г., 12:00»), а строка обязана совпасть
  // с серверной дословно.
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');

  return `${datePart}, ${hours}:${minutes} ${UTC_LABEL}`;
}
